import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { format, startOfMonth, subDays, subMonths, endOfMonth } from "date-fns";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getTenantReport } from "../../actions/tenant";
import TenantTrendChart from "./TenantTrendChart";
import { money, count } from "./tenantLabels";

// A tenant's own page: income, refunds (the only expense kept) and prints of
// the kiosks they rent, each from the day it became theirs. They only read.
// The backend works out the report (GET /api/monitoring/tenant-report); days are
// Mongolian days, as the browser here reads them. Prints from page bundles are in
// the income at what their bundle cost a page, and also shown apart.

const dayOf = (date) => format(date, "yyyy-MM-dd");

const RANGES = [
  { key: "today", label: "Өнөөдөр", range: (now) => [now, now] },
  { key: "7days", label: "7 хоног", range: (now) => [subDays(now, 6), now] },
  { key: "30days", label: "30 хоног", range: (now) => [subDays(now, 29), now] },
  { key: "month", label: "Энэ сар", range: (now) => [startOfMonth(now), now] },
  {
    key: "lastMonth",
    label: "Өмнөх сар",
    range: (now) => [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
  },
];
const DEFAULT_RANGE = "30days";

const card = "bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700";

const StatCard = ({ title, value, note }) => (
  <div className={"p-5 " + card}>
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
    <p className="mt-2 text-2xl font-semibold tracking-tight text-gray-800 truncate dark:text-gray-100">
      {value}
    </p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</p>
  </div>
);

const num = "px-4 py-3 text-right tabular-nums";
const numDay = "px-4 py-2 text-right tabular-nums";

const TenantDashboard = ({ getTenantReport, tenant: { report, updatedAt, reportLoading, reportError } }) => {
  // The range the page opens with, as ["YYYY-MM-DD", "YYYY-MM-DD"]
  const [initial] = useState(() =>
    RANGES.find((item) => item.key === DEFAULT_RANGE).range(new Date()).map(dayOf)
  );
  const [from, setFrom] = useState(initial[0]);
  const [to, setTo] = useState(initial[1]);
  const [active, setActive] = useState(DEFAULT_RANGE);

  useEffect(() => {
    getTenantReport(initial[0], initial[1]);
  }, [getTenantReport, initial]);

  const pickRange = (option) => {
    const [start, end] = option.range(new Date());
    setFrom(dayOf(start));
    setTo(dayOf(end));
    setActive(option.key);
    getTenantReport(dayOf(start), dayOf(end));
  };

  const submitCustom = (e) => {
    e.preventDefault();
    if (!from || !to || from > to) return;
    setActive("custom");
    getTenantReport(from, to);
  };

  const totals = report && report.totals;
  const days = (report && report.days) || [];

  return (
    <Admin>
      {/* min-w-0: the layout's grid would otherwise widen the page to the
          tables' width on a phone, instead of letting them scroll */}
      <div className="min-w-0 pb-10">
        <div className="flex flex-col mt-6 mb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">
              Хяналтын самбар
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Таны түрээсийн киоскуудын орлого, зарлага, хэвлэлт
            </p>
          </div>
          {updatedAt && (
            <p className="mt-3 text-xs text-gray-400 sm:mt-0 dark:text-gray-500">
              Шинэчилсэн: {format(new Date(updatedAt), "HH:mm")}
            </p>
          )}
        </div>

        {/* One row of filters; everything below follows it */}
        <section className={"p-4 mb-6 " + card}>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="flex flex-wrap gap-2">
              {RANGES.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => pickRange(option)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                    active === option.key
                      ? "text-white bg-purple-600 shadow-sm"
                      : "text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <form onSubmit={submitCustom} className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Эхлэх огноо
                <input
                  type="date"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  className="block w-full mt-1 text-sm text-gray-700 bg-white border-gray-300 rounded-lg dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600"
                />
              </label>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Дуусах огноо
                <input
                  type="date"
                  value={to}
                  min={from}
                  max={dayOf(new Date())}
                  onChange={(e) => setTo(e.target.value)}
                  className="block w-full mt-1 text-sm text-gray-700 bg-white border-gray-300 rounded-lg dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600"
                />
              </label>
              <button
                type="submit"
                disabled={reportLoading || !from || !to || from > to}
                className="h-10 px-5 text-sm font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-800 dark:bg-gray-100"
              >
                Харах
              </button>
            </form>
          </div>
        </section>

        {reportError && (
          <div className="px-4 py-3 mb-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50 dark:text-red-200 dark:bg-red-900 dark:border-red-700">
            Тайланг шинэчилж чадсангүй.
            {report && " Өмнөх мэдээллийг харуулж байна."}
          </div>
        )}

        {!report ? (
          reportLoading && (
            <img src={spinner} className="my-6" style={{ width: "50px", margin: "auto", display: "block" }} alt="Loading..." />
          )
        ) : (
          // The last report stays, dimmed, while the next one loads
          <div className={"transition-opacity " + (reportLoading ? "opacity-50" : "")}>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {report.from === report.to ? report.from : `${report.from} — ${report.to}`}
            </p>

            <div className={"grid gap-4 sm:grid-cols-2 xl:grid-cols-4 " + (totals.bundlePages > 0 ? "mb-3" : "mb-6")}>
              <StatCard
                title="Орлого"
                value={money(totals.income)}
                note={
                  `${count(totals.payments)} гүйлгээ` +
                  (totals.bundlePages > 0 ? ` · Үүнээс багцаар ${money(totals.bundleIncome)}` : "")
                }
              />
              <StatCard title="Зарлага (буцаалт)" value={money(totals.refund)} note="Хэрэглэгчид буцаан олгосон" />
              <StatCard title="Цэвэр орлого" value={money(totals.net)} note="Орлогоос буцаалтыг хассан" />
              <StatCard
                title="Хэвлэсэн хуудас"
                value={count(totals.pages)}
                note={
                  `Хар цагаан ${count(totals.monoPages)} · Өнгөт ${count(totals.colorPages)}` +
                  (totals.photoPages > 0 ? ` · Зураг ${count(totals.photoPages)}` : "")
                }
              />
            </div>
            {totals.bundlePages > 0 && (
              <p className="mb-6 text-xs text-gray-500 dark:text-gray-400">
                Багцаар хэвлэсэн {count(totals.bundlePages)} хуудсыг тухайн багцын нэг хуудасны үнээр (багцын үнэ ÷
                хуудасны тоо) орлогод тооцсон. Багцын үнэгүй зураг орлогогүй.
              </p>
            )}

            {days.length > 1 && (
              <div className="grid gap-4 mb-6 lg:grid-cols-2">
                <section className={"p-5 " + card}>
                  <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">Цэвэр орлого, өдрөөр</h2>
                  <TenantTrendChart days={days} field="net" name="Цэвэр орлого" format={money} />
                </section>
                <section className={"p-5 " + card}>
                  <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">Хэвлэсэн хуудас, өдрөөр</h2>
                  <TenantTrendChart days={days} field="pages" name="Хуудас" format={count} />
                </section>
              </div>
            )}

            <section className={"mb-6 overflow-hidden " + card}>
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">Киоскоор</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Киоск бүр танд оноогдсон өдрөөс тоологдоно
                </p>
              </div>
              {report.kiosks.length === 0 ? (
                <p className="px-5 py-10 text-sm text-center text-gray-500 dark:text-gray-400">
                  Танд оноосон киоск алга.
                </p>
              ) : (
                <div className="w-full overflow-x-auto">
                  <table className="w-full whitespace-nowrap">
                    <thead>
                      <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase bg-gray-50 dark:text-gray-400 dark:bg-gray-900">
                        <th className="px-4 py-3">Киоск</th>
                        <th className="px-4 py-3 text-right">Гүйлгээ</th>
                        <th className="px-4 py-3 text-right">Хуудас</th>
                        <th className="px-4 py-3 text-right">Орлого</th>
                        <th className="px-4 py-3 text-right">Буцаалт</th>
                        <th className="px-4 py-3 text-right">Цэвэр орлого</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm text-gray-700 divide-y divide-gray-100 dark:text-gray-300 dark:divide-gray-700">
                      {report.kiosks.map((kiosk) => (
                        <tr key={kiosk.id}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800 dark:text-gray-100">{kiosk.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {kiosk.location || "Байршилгүй"}
                              {kiosk.since && ` · ${kiosk.since}-с`}
                            </p>
                          </td>
                          <td className={num}>{count(kiosk.payments)}</td>
                          <td className={num}>
                            {count(kiosk.pages)}
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              хар {count(kiosk.monoPages)} · өнгөт {count(kiosk.colorPages)}
                              {kiosk.photoPages > 0 && ` · зураг ${count(kiosk.photoPages)}`}
                            </p>
                          </td>
                          <td className={num}>
                            {money(kiosk.income)}
                            {kiosk.bundlePages > 0 && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                багцаар {money(kiosk.bundleIncome)}
                              </p>
                            )}
                          </td>
                          <td className={num}>{money(kiosk.refund)}</td>
                          <td className={num + " font-semibold text-gray-900 dark:text-white"}>{money(kiosk.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="text-sm font-semibold text-gray-800 bg-gray-50 dark:text-gray-100 dark:bg-gray-900">
                        <td className="px-4 py-3">Нийт</td>
                        <td className={num}>{count(totals.payments)}</td>
                        <td className={num}>{count(totals.pages)}</td>
                        <td className={num}>{money(totals.income)}</td>
                        <td className={num}>{money(totals.refund)}</td>
                        <td className={num}>{money(totals.net)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>

            {/* The charts' values without hovering */}
            {days.length > 1 && (
              <details className={"overflow-hidden " + card}>
                <summary className="px-5 py-4 text-sm font-semibold text-gray-700 cursor-pointer dark:text-gray-200">
                  Өдөр тутмын дэлгэрэнгүй
                </summary>
                <div className="w-full overflow-x-auto border-t border-gray-100 dark:border-gray-700">
                  <table className="w-full whitespace-nowrap">
                    <thead>
                      <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase bg-gray-50 dark:text-gray-400 dark:bg-gray-900">
                        <th className="px-4 py-3">Өдөр</th>
                        <th className="px-4 py-3 text-right">Гүйлгээ</th>
                        <th className="px-4 py-3 text-right">Хуудас</th>
                        <th className="px-4 py-3 text-right">Орлого</th>
                        <th className="px-4 py-3 text-right">Буцаалт</th>
                        <th className="px-4 py-3 text-right">Цэвэр орлого</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm text-gray-700 divide-y divide-gray-100 dark:text-gray-300 dark:divide-gray-700">
                      {days.map((day) => (
                        <tr key={day.day}>
                          <td className="px-4 py-2 tabular-nums">{day.day}</td>
                          <td className={numDay}>{count(day.payments)}</td>
                          <td className={numDay}>{count(day.pages)}</td>
                          <td className={numDay}>{money(day.income)}</td>
                          <td className={numDay}>{money(day.refund)}</td>
                          <td className={numDay}>{money(day.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </Admin>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  note: PropTypes.string.isRequired,
};

TenantDashboard.propTypes = {
  getTenantReport: PropTypes.func.isRequired,
  tenant: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  tenant: state.tenant,
});

export default connect(mapStateToProps, { getTenantReport })(TenantDashboard);
